import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TrainingProgressBar from '../components/training/TrainingProgressBar';
import ModuleViewer from '../components/training/ModuleViewer';
import TestViewer from '../components/training/TestViewer';
import TestResults from '../components/training/TestResults';

/**
 * Training Mode Page
 * Main page for the training system with 14 modules in 4 parts
 */
const TrainingMode = () => {
  const [modules, setModules] = useState([]);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // overview, module, test, results
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentTest, setCurrentTest] = useState(null);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    loadTrainingData();
  }, []);

  const loadTrainingData = async () => {
    try {
      setLoading(true);

      const [modulesRes, statusRes] = await Promise.all([
        api.get('/api/training/modules'),
        api.get('/api/training/status'),
      ]);

      console.log('Training data loaded:', {
        modules: modulesRes.data.modules?.length,
        status: statusRes.data.status
      });

      setModules(modulesRes.data.modules || []);
      setTrainingStatus(statusRes.data.status);
      setLoading(false);
    } catch (error) {
      console.error('Error loading training data:', error);
      setLoading(false);
    }
  };

  const handleModuleClick = async (module) => {
    if (module.progress?.status === 'locked') {
      alert('Сначала завершите предыдущий модуль');
      return;
    }

    setSelectedModule(module);
    setView('module');

    // Start module if not started
    if (module.progress?.status !== 'in_progress' && module.progress?.status !== 'completed') {
      try {
        await api.post(`/api/training/modules/${module.id}/start`);
        await loadTrainingData();
      } catch (error) {
        console.error('Error starting module:', error);
      }
    }
  };

  const handleTakeTest = async () => {
    try {
      const response = await api.get(`/api/training/modules/${selectedModule.id}/test`);

      if (!response.data.canTake) {
        alert(response.data.canTakeReason || 'Невозможно пройти тест');
        return;
      }

      setCurrentTest(response.data.test);
      setView('test');
    } catch (error) {
      console.error('Error loading test:', error);
      alert('Ошибка при загрузке теста');
    }
  };

  const handleTestComplete = (result) => {
    setTestResults(result);
    setView('results');
    loadTrainingData(); // Reload to update progress
  };

  const handleRetakeTest = () => {
    setTestResults(null);
    setView('module');
  };

  const handleContinue = () => {
    setSelectedModule(null);
    setCurrentTest(null);
    setTestResults(null);
    setView('overview');
  };

  const getModulesByPart = (partNumber) => {
    return modules.filter((m) => m.part_number === partNumber);
  };

  const getPartProgress = (partNumber) => {
    const partModules = getModulesByPart(partNumber);
    const completed = partModules.filter((m) => m.progress?.status === 'completed').length;
    const total = partModules.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getOverallProgress = () => {
    const completed = modules.filter((m) => m.progress?.status === 'completed').length;
    const total = modules.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getModuleStatusIcon = (status) => {
    if (status === 'completed') {
      return (
        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    } else if (status === 'in_progress') {
      return (
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    }
  };

  const getModuleButtonText = (status) => {
    if (status === 'completed') return 'Повторить';
    if (status === 'in_progress') return 'Продолжить';
    if (status === 'locked') return 'Заблокировано';
    return 'Начать';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка обучения...</p>
        </div>
      </div>
    );
  }

  // Module View
  if (view === 'module' && selectedModule) {
    return (
      <ModuleViewer
        moduleId={selectedModule.id}
        onTakeTest={handleTakeTest}
        onBack={handleContinue}
      />
    );
  }

  // Test View
  if (view === 'test' && currentTest) {
    return (
      <TestViewer
        test={currentTest}
        moduleId={selectedModule.id}
        onTestComplete={handleTestComplete}
      />
    );
  }

  // Results View
  if (view === 'results' && testResults) {
    return (
      <TestResults
        result={testResults}
        onRetake={handleRetakeTest}
        onContinue={handleContinue}
      />
    );
  }

  // Overview
  const overallProgress = getOverallProgress();
  const parts = [
    { number: 1, title: 'Основы продаж', color: 'blue' },
    { number: 2, title: 'Техники работы', color: 'purple' },
    { number: 3, title: 'Продукт и услуги', color: 'green' },
    { number: 4, title: 'Мастерство продаж', color: 'orange' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Режим обучения</h1>
          <p className="text-gray-600 mb-6">
            Пройдите все 14 модулей обучения, чтобы получить доступ к практическим тренировкам
          </p>

          <TrainingProgressBar progress={overallProgress} showLabel={true} height="h-4" />

          {trainingStatus?.training_completed && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-semibold">
                Поздравляем! Вы завершили обучение и можете приступать к практическим тренировкам.
              </p>
            </div>
          )}
        </div>

        {/* Parts */}
        {parts.map((part) => {
          const partModules = getModulesByPart(part.number);
          const partProgress = getPartProgress(part.number);

          return (
            <div key={part.number} className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Часть {part.number}: {part.title}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {partModules.filter((m) => m.progress?.status === 'completed').length} из {partModules.length} модулей завершено
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold text-${part.color}-600`}>
                    {partProgress}%
                  </div>
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div
                  className={`bg-gradient-to-r from-${part.color}-500 to-${part.color}-600 h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${partProgress}%` }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {partModules.map((module) => {
                  const status = module.progress?.status || 'pending';
                  const isLocked = status === 'locked';

                  return (
                    <div
                      key={module.id}
                      className={`border-2 rounded-lg p-4 transition-all ${
                        isLocked
                          ? 'border-gray-200 bg-gray-50 opacity-60'
                          : 'border-gray-200 hover:border-blue-400 hover:shadow-md cursor-pointer'
                      }`}
                      onClick={() => !isLocked && handleModuleClick(module)}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mr-3">
                          {getModuleStatusIcon(status)}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 mb-1">
                            Модуль {module.module_number}: {module.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">{module.description}</p>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${
                              status === 'completed' ? 'bg-green-100 text-green-700' :
                              status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              isLocked ? 'bg-gray-100 text-gray-500' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {getModuleButtonText(status)}
                            </span>
                            {module.progress?.best_score !== undefined && module.progress.best_score !== null && (
                              <span className="text-xs text-gray-500">
                                Лучший результат: {module.progress.best_score}%
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrainingMode;
